import { useState } from 'react';

import { Dropdown } from 'antd';

import Button from '@/components/shared/Button/Button';
import DynamicSVGColor from '@/components/shared/DynamicSVGColor/DynamicSVGColor';
import { TextField } from '@/components/shared/TextField/TextField';
import { ExploreBlock, ExploreBlockType } from '@addmile/backend-client';

import { BLOCK_TYPES } from '../../../../Content/const';
import { blockInfo } from '../../../blockInfo';
import { CHECK_IN_ICON } from '../../../const';
import { JourneyDraft, StepDraft, UpdateDraft, newCheckInDraft } from '../../../draft';
import { DeleteTarget, Selection } from '../../../selection';
import BlockPickerModal from '../BlockPickerModal/BlockPickerModal';
import Field from '../Form/Field';
import Section from '../Form/Section';
import MoveButtons from '../MoveButtons/MoveButtons';
import PanelHeader from '../PanelHeader/PanelHeader';
import { toBlockContent } from './toBlockContent';

const CHECK_IN_MENU_KEY = 'check-in';

interface StepPanelProps {
  draft: JourneyDraft;
  mileIndex: number;
  stepIndex: number;
  update: UpdateDraft;
  locked: boolean;
  onSelect: (selection: Selection) => void;
  onDelete: (target: DeleteTarget) => void;
}

const StepPanel = ({
  draft,
  mileIndex,
  stepIndex,
  update,
  locked,
  onSelect,
  onDelete,
}: StepPanelProps) => {
  const [pickerType, setPickerType] = useState<ExploreBlockType>();
  const mile = draft.miles[mileIndex];
  const step = mile.steps[stepIndex];
  // The backend keeps a check-in as the last block of its step, so there is at most one.
  const hasCheckIn = step.blocks.some((block) => block.checkIn);
  const movableCount = hasCheckIn ? step.blocks.length - 1 : step.blocks.length;

  const editStep = (recipe: (step: StepDraft) => void) =>
    update((d) => recipe(d.miles[mileIndex].steps[stepIndex]));

  const moveBlock = (from: number, to: number) =>
    editStep((s) => {
      const [block] = s.blocks.splice(from, 1);
      s.blocks.splice(to, 0, block);
    });

  const addBlock = (block: ExploreBlock) => {
    editStep((s) => {
      const checkInIndex = s.blocks.findIndex((b) => b.checkIn);
      const added = { key: crypto.randomUUID(), content: toBlockContent(block) };
      s.blocks.splice(checkInIndex === -1 ? s.blocks.length : checkInIndex, 0, added);
    });
    setPickerType(undefined);
  };

  const addCheckIn = () => {
    const checkIn = newCheckInDraft();
    editStep((s) => void s.blocks.push(checkIn));
    onSelect({ kind: 'block', mileKey: mile.key, stepKey: step.key, blockKey: checkIn.key });
  };

  const addBlockMenu = {
    items: [
      ...BLOCK_TYPES.map(({ value, label, icon }) => ({
        key: value,
        label,
        icon: <img src={icon} alt="" className="w-5 h-5" />,
      })),
      {
        key: CHECK_IN_MENU_KEY,
        label: 'Check-in',
        icon: <img src={CHECK_IN_ICON} alt="" className="w-5 h-5" />,
        disabled: hasCheckIn,
      },
    ],
    onClick: ({ key }: { key: string }) =>
      key === CHECK_IN_MENU_KEY ? addCheckIn() : setPickerType(key as ExploreBlockType),
  };

  return (
    <div className="grid gap-6">
      <BlockPickerModal
        type={pickerType}
        onPick={addBlock}
        onClose={() => setPickerType(undefined)}
      />

      <PanelHeader
        eyebrow={`Mile ${mileIndex + 1} · Step ${mileIndex + 1}.${stepIndex + 1}`}
        title={step.title || 'Untitled step'}
        deleteLabel="Delete step"
        onDelete={
          locked
            ? undefined
            : () => onDelete({ kind: 'step', mileKey: mile.key, stepKey: step.key })
        }
      />

      <Section title="General Info">
        <Field label="Title" hint="Headline of every block in the step, and of the step’s cover">
          <TextField
            className="!w-full"
            inputClassName="!font-normal"
            value={step.title}
            placeholder="e.g. Reaching and Pulling Away"
            onChange={(e) => editStep((s) => void (s.title = e.target.value))}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-neutral-80 cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={step.showFeedback}
            onChange={(e) => editStep((s) => void (s.showFeedback = e.target.checked))}
          />
          Ask for a rating when the step is done
        </label>
      </Section>

      <div className="grid gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-90">
              Blocks <span className="text-neutral-40">({step.blocks.length})</span>
            </h3>
            <p className="text-sm text-neutral-50">
              Users walk the blocks in this order. A check-in always comes last.
            </p>
          </div>
          {!locked && (
            <Dropdown menu={addBlockMenu} trigger={['click']} placement="bottomRight">
              <span>
                <Button
                  label="Add Block"
                  size="s"
                  className="!w-fit px-4"
                  prefixIcon={
                    <DynamicSVGColor icon="/icons/plus.svg" className="w-5 h-5 !bg-white-100" />
                  }
                />
              </span>
            </Dropdown>
          )}
        </div>

        {!step.blocks.length && (
          <p className="text-sm text-neutral-50">
            No blocks yet. Add Explore blocks, and a check-in to close the step if it needs one.
          </p>
        )}

        {step.blocks.map((block, blockIndex) => {
          const { icon, typeLabel, title } = blockInfo(block);
          const selectBlock = () =>
            onSelect({ kind: 'block', mileKey: mile.key, stepKey: step.key, blockKey: block.key });

          return (
            <div
              key={block.key}
              onClick={selectBlock}
              className="flex items-center gap-3 rounded-xl bg-neutral-10 px-3 py-2 cursor-pointer"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white-100 shrink-0">
                {icon && <img src={icon} alt="" className="w-6 h-6" />}
              </span>
              {!locked && (
                <MoveButtons
                  canMoveUp={!block.checkIn && blockIndex > 0}
                  canMoveDown={!block.checkIn && blockIndex < movableCount - 1}
                  onMoveUp={() => moveBlock(blockIndex, blockIndex - 1)}
                  onMoveDown={() => moveBlock(blockIndex, blockIndex + 1)}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-neutral-90 truncate">{title}</p>
                <p className="text-sm text-neutral-50">{typeLabel}</p>
              </div>
              {!locked && (
                <span onClick={(e) => e.stopPropagation()}>
                  <Button
                    isIcon
                    className="border-none shrink-0"
                    variant="transparent"
                    prefixIcon={<img src="/icons/trash.svg" alt="delete block" />}
                    onClick={() =>
                      onDelete({
                        kind: 'block',
                        mileKey: mile.key,
                        stepKey: step.key,
                        blockKey: block.key,
                      })
                    }
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepPanel;
